import { t } from "@travily/i18n";
import individualIllustration from "@ui/assets/illustrations/individual-animated.webp";
import { TileRadioGroup } from "@ui/components/TileRadioGroup";
import { Wizard } from "@ui/components/Wizard";
import { useBreakpoint } from "@ui/hooks/use-breakpoint";
import type { ReactNode } from "react";

const COLOR = "purple" as const;

export type PrivacyChoice = "public" | "private";

export interface IndividualPrivacyStepViewProps {
  /** Wizard progress: 1-based index of the current stage. */
  currentStage: number;
  /** Wizard progress: total number of stages. */
  totalStages: number;
  /** Navigate to the previous stage. Omit to hide the back button. */
  onBack?: () => void;
  /** Top-bar tertiary actions (e.g. "Finish later"). */
  topBarActions: ReactNode;
  /** Advance to / submit the current stage. */
  onPrimary: () => void;
  /** Whether the primary button shows a pending spinner. */
  primaryPending: boolean;
  /** Currently selected visibility choice. */
  value: PrivacyChoice;
  /** Called with the newly selected visibility choice. */
  onChange: (value: PrivacyChoice) => void;
}

export function IndividualPrivacyStepView({
  currentStage,
  totalStages,
  onBack,
  topBarActions,
  onPrimary,
  primaryPending,
  value,
  onChange,
}: IndividualPrivacyStepViewProps) {
  const isAboveMd = useBreakpoint("md");
  const isAboveLg = useBreakpoint("lg");
  const selectionGroupSize = isAboveLg ? "5xl" : isAboveMd ? "4xl" : "3xl";

  const radioItems = [
    { value: "public", label: t("web.individuals.creator.steps.privacy.show.options.public") },
    { value: "private", label: t("web.individuals.creator.steps.privacy.show.options.private") },
  ];

  return (
    <Wizard color={COLOR} currentStage={currentStage} totalStages={totalStages} onBack={onBack}>
      {topBarActions}
      <Wizard.PrimaryButton onPress={onPrimary} isPending={primaryPending}>
        {t("common.actions.continue")}
      </Wizard.PrimaryButton>
      <Wizard.Stage
        illustration={<img alt="" aria-hidden="true" className="h-32 w-auto" src={individualIllustration} />}
        heading={t("web.individuals.creator.steps.privacy.show.title")}
      >
        <TileRadioGroup
          className="w-full max-w-[600px] gap-5"
          color={COLOR}
          onChange={(v: string) => onChange(v as PrivacyChoice)}
          size={selectionGroupSize}
          value={value}
        >
          {radioItems.map((item) => (
            <TileRadioGroup.Item key={item.value} value={item.value}>
              {item.label}
            </TileRadioGroup.Item>
          ))}
        </TileRadioGroup>
      </Wizard.Stage>
    </Wizard>
  );
}
